import { useRef, useLayoutEffect } from "react";
import { useSprings, animated, to } from "react-spring";
import PlayingCard from "./PlayingCard";

// Renders a player's hand and FLIP-animates the remaining cards whenever one
// is removed (played), so they slide together to close the gap instead of
// snapping instantly. Each card is measured (by its stable card code) before
// and after a hand change; the delta between old and new position is played
// back as a spring from that offset down to zero.
//
// Positions are read via offsetLeft/offsetTop rather than
// getBoundingClientRect(). Player 2/4's hands sit inside a rotated
// container (transform: rotate(+-90deg) for the side-of-table display) -
// getBoundingClientRect() reports the rotated, on-screen box, which doesn't
// match the local (pre-rotation) space a transform: translate() on the
// card itself animates in, causing a wrong (rotated) and often much too
// large correction. offsetLeft/offsetTop reflect layout position and are
// unaffected by transform, so they stay correct regardless of rotation.
const PlayerHand = ({ hand, showAllCards, ...cardProps }) => {
  const nodesRef = useRef(new Map()); // card code -> DOM node
  const prevPositionsRef = useRef(new Map()); // card code -> {left, top}

  const [springs, api] = useSprings(hand.length, () => ({ x: 0, y: 0 }));

  useLayoutEffect(() => {
    const newPositions = new Map();
    hand.forEach((card) => {
      const node = nodesRef.current.get(card.details.card);
      if (node)
        newPositions.set(card.details.card, {
          left: node.offsetLeft,
          top: node.offsetTop,
        });
    });

    api.start((i) => {
      const cardCode = hand[i]?.details.card;
      const newPos = newPositions.get(cardCode);
      const oldPos = prevPositionsRef.current.get(cardCode);

      if (oldPos && newPos) {
        const dx = oldPos.left - newPos.left;
        const dy = oldPos.top - newPos.top;
        if (dx || dy) {
          return { from: { x: dx, y: dy }, to: { x: 0, y: 0 } };
        }
      }
      return { x: 0, y: 0, immediate: true };
    });

    prevPositionsRef.current = newPositions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hand.map((card) => card.details.card).join(",")]);

  return hand.map((card, i) => (
    <animated.div
      key={card.details.card}
      style={{
        display: "inline-block",
        transform: to(
          [springs[i].x, springs[i].y],
          (x, y) => `translate(${x}px, ${y}px)`
        ),
      }}
    >
      <PlayingCard
        ref={(node) => {
          if (node) nodesRef.current.set(card.details.card, node);
          else nodesRef.current.delete(card.details.card);
        }}
        card={card.details.card}
        url={card.details.url}
        faceUp={card.details.faceUp || showAllCards}
        suit={card.suit}
        alt={card.details.card}
        shouldDarken={card.shouldDarken}
        {...cardProps}
      />
    </animated.div>
  ));
};

export default PlayerHand;
